/**
 * Shared helper for inserting new entities into conversation message lists.
 * Provides common logic for placing entities relative to parent events.
 */

import type { ChatMessageEntity, MessageEntity } from './chat-message-entity.interface'
import type { ChatState } from './chat-state.interface'

export interface InsertEntityParams {
  state: ChatState
  conversationId: string
  parentEventId: string
  entity: ChatMessageEntity
}

export const insertEntityAfterParent = ({
  state,
  conversationId,
  parentEventId,
  entity,
}: InsertEntityParams): void => {
  state.messageEntities[entity.id] = entity

  if (!state.messageIdsByConversation[conversationId]) {
    state.messageIdsByConversation[conversationId] = []
  }

  const idList = state.messageIdsByConversation[conversationId]
  const parentIndex = idList.indexOf(parentEventId)

  if (parentIndex === -1) {
    idList.push(entity.id)
    return
  }

  idList.splice(parentIndex + 1, 0, entity.id)
}

export interface AppendEntityParams {
  state: ChatState
  conversationId: string
  entity: ChatMessageEntity
}

export const appendEntityToConversation = ({
  state,
  conversationId,
  entity,
}: AppendEntityParams): void => {
  state.messageEntities[entity.id] = entity

  if (!state.messageIdsByConversation[conversationId]) {
    state.messageIdsByConversation[conversationId] = []
  }

  state.messageIdsByConversation[conversationId].push(entity.id)
}

export interface CreateStreamMessageParams {
  conversationId: string
  parentEventId: string
  eventId: string
  agentName?: string
  threadId?: string
  messageRunId?: string
  turnStartedAt?: string
  agentLabel?: string
  parentThreadId?: string | null
}

/**
 */
export const insertStreamingEntityAfterParent = (
  state: ChatState,
  params: CreateStreamMessageParams
): void => {
  const {
    conversationId,
    parentEventId,
    eventId,
    agentName,
    threadId,
    messageRunId,
    turnStartedAt,
    agentLabel,
    parentThreadId,
  } = params

  if (state.messageEntities[eventId]) {
    return
  }

  const displayName = agentLabel ?? agentName ?? 'Assistant'

  const entity: MessageEntity = {
    id: eventId,
    conversationId,
    type: 'message',
    content: '',
    timestamp: new Date().toISOString(),
    sender: 'assistant',
    participant: { id: agentName ?? 'assistant', name: displayName },
    isPending: false,
    isStreaming: true,
    streamingContent: '',
    threadId,
    messageRunId,
    turnStartedAt: turnStartedAt ?? new Date().toISOString(),
    agentLabel,
    parentThreadId,
  }

  insertEntityAfterParent({ state, conversationId, parentEventId, entity })
}
